import {
  Component,
  h,
  Element,
  Listen,
  Prop,
  State,
  Watch,
  Host,
} from "@stencil/core"
import {
  selectReducer,
  getInitialState,
  SelectState,
  SelectActions,
  isKeyboardBehavior,
  getDisplayText,
} from "../select-store"
import { Strategy } from "../types"
import {
  StoreConnection,
  StateHandle,
  computedProperty,
  getAppearance,
} from "../../../core/helpers/webcomponent-helpers"

@Component({
  tag: "sdx-select-option",
  styleUrl: "select-option.scss",
  shadow: true,
})
export class SelectOption {
  private store: StoreConnection<this, SelectState, SelectActions>
  private selectedChangedInProgress = false
  private selectionChangedInProgress = false

  // A "native" hidden DOM element that is submitted when the item is used in a
  // traditional form.
  private lightDOMHiddenFormInputEl?: HTMLInputElement

  @Element() public el!: HTMLSdxSelectOptionElement

  @State() public state!: StateHandle<SelectState>

  /**
   * Value of the option that will be added to the sdx-select's value when
   * selected.
   */
  @Prop() public value: any

  /**
   * Text to show within the sdx-select when selected. If not provided, the
   * option's `.textContent` is displayed. This property should only be used in
   * rare cases, e.g. when HTML is used to decorate the option with additinal
   * HTML.
   * When used in combination with `keyboard-behavior="autocomplete"`, the
   * sdx-select's `filterFunction` might be needed, which by default also
   * filters the option's `.textContent`.
   */
  @Prop() public displayText?: string

  /**
   * Whether this option is initially selected.
   */
  @Prop({ mutable: true }) public selected: boolean = false

  /**
   * Not selectable (event propagation will be stopped).
   */
  @Prop() public disabled: boolean = false

  /**
   * Whether this option is the placeholder element.
   * @private
   */
  @Prop() public placeholder: boolean = false

  @Watch("state")
  public stateChanged({}, prev: StateHandle<SelectState>) {
    const state = this.state.get()
    const prevState = prev.get()

    if (state.selection !== prevState.selection) {
      this.selectionChanged()
    }

    if (state.name !== prevState.name) {
      this.nameChanged()
    }
  }

  @Watch("selected")
  public selectedChanged() {
    this.selectedChangedInProgress = true

    if (!this.selectionChangedInProgress) {
      this.select(this.selected ? "add" : "remove")
      this.updateHiddenFormInputEl()
    }

    this.selectedChangedInProgress = false
  }

  @Listen("click")
  public onClick() {
    const state = this.state.get()
    const closeAutomatically = this.shouldCloseAutomatically()

    if (this.disabled) {
      if (closeAutomatically) {
        state.close()
      }
    } else {
      // Don't select anything if "autocomplete", only set the filter keyword
      if (isKeyboardBehavior(state, "autocomplete")) {
        const value = getDisplayText(this.el)

        this.store.set("userInteractionInProgress", true)
        this.store.set("filterInputElValue", value)
        this.store.set("userInteractionInProgress", false)

        // Close with a little delay for the user to see what was selected
        setTimeout(() => {
          // Apply filter *after* the autocomplete has closed in order to
          // prevent glitches. Otherwise, for a brief moment, all results are
          // being displayed again.
          state
            .close()
            .then(() =>
              this.store.dispatch({ type: "SET_FILTER", filter: value })
            )
        }, state.animationDuration)

        return
      }

      const alreadySelected = this.isSelected()

      this.store.set("userInteractionInProgress", true)

      this.select("add")

      if (closeAutomatically) {
        // Close with a little delay for the user to see what was selected
        setTimeout(() => {
          state.close().then(() => {
            // Clear the filter after an option was selected (that wasn't
            // selected before)
            if (alreadySelected) {
              return
            }

            this.store.set("filterInputElValue", "")
            this.store.dispatch({ type: "SET_FILTER", filter: "" })
          })
        }, state.animationDuration)
      }
    }
  }

  constructor() {
    this.store = new StoreConnection(this, selectReducer, getInitialState(), [
      "selection",
      "multiple",
      "directionX",
      "directionY",
      "name",
      "filter",
      "animationDuration",
      "close",
      "keyboardBehavior",
      "isInsideDialog",
    ])
  }

  public connectedCallback() {
    // Don't do anything while the component is being reappended (maybe
    // because it's being appended to document.body), but send a "ping" for
    // each child (sdx-select-option).
    // After each child is has finished, "reappending" will be marked "done" by
    // the store.
    if (this.state.get().reappending) {
      this.store?.dispatch({ type: "UPDATE_REAPPENDING" })
      return
    }

    // Register self (except placeholder)
    this.store.dispatch({ type: "ADD_OPTION_EL", optionEl: this.el })

    if (this.selected) {
      this.select("add")
    }
  }

  public disconnectedCallback() {
    // Performance: stop if the parent isn't even connected (e.g. *ngIf)
    if (!this.state.get().connected) {
      return
    }

    // See connectedCallback() for explanation
    if (this.state.get().reappending) {
      return
    }

    // Unregister self
    this.store.dispatch({ type: "REMOVE_OPTION_EL", optionEl: this.el })

    if (!this.el.isConnected && this.selected) {
      this.select("remove")
    }
  }

  public componentDidLoad() {
    this.store.subscribe()
  }

  private selectionChanged() {
    this.selectionChangedInProgress = true

    if (!this.selectedChangedInProgress) {
      this.selected = this.isSelected()
      this.updateHiddenFormInputEl()
    }

    this.selectionChangedInProgress = false
  }

  private nameChanged() {
    this.updateHiddenFormInputEl()
  }

  private updateHiddenFormInputEl(): void {
    const state = this.state.get()

    // See sdx-select for more information.
    if (isKeyboardBehavior(state, "autocomplete")) {
      return
    }

    if (!this.lightDOMHiddenFormInputEl) {
      this.lightDOMHiddenFormInputEl = document.createElement("input")
      this.lightDOMHiddenFormInputEl.type = "hidden"
      this.el.append(this.lightDOMHiddenFormInputEl)
    }

    // Clean up properties added previously in case they've changed
    this.lightDOMHiddenFormInputEl.removeAttribute("name")
    this.lightDOMHiddenFormInputEl.removeAttribute("value")

    if (state.name && this.selected) {
      this.lightDOMHiddenFormInputEl.name = state.name
      this.lightDOMHiddenFormInputEl.value = this.value || this.el.textContent
    }
  }

  private select(strategy: Strategy): void {
    this.store.dispatch({
      type: "SELECT",
      optionEl: this.placeholder ? null : this.el,
      strategy,
    })
  }

  public isSelected(): boolean {
    return !!this.state.get().selection.includes(this.el)
  }

  private shouldCloseAutomatically() {
    const { multiple, isInsideDialog } = this.state.get()
    return !(multiple || isInsideDialog)
  }

  private getHostClassNames() {
    const { multiple, directionY, isInsideDialog } = this.state.get()

    return {
      multiple,
      [getAppearance(this.el)]: true,
      selected: this.isSelected(),
      disabled: this.disabled,
      ...computedProperty(directionY),
      "inside-dialog": isInsideDialog,
    }
  }

  private getInputClassNames() {
    const { multiple } = this.state.get()

    return {
      input: true,
      checkbox: multiple,
      radio: !multiple,
      [getAppearance(this.el)]: true,
    }
  }

  public render() {
    const inputClassNames = this.getInputClassNames()
    const { checkbox } = inputClassNames

    return (
      <Host class={this.getHostClassNames()}>
        <div class="component">
          <div class="option">
            <div class={inputClassNames}>
              {/* <sdx-input-item> is too slow with 1000+ items (even
                  <sdx-dummy is too slow - it's Stencil, see
                  https://github.com/ionic-team/stencil/issues/3569) */}
              <input
                id="input"
                type={checkbox ? "checkbox" : "radio"}
                checked={this.isSelected()}
                disabled={this.disabled}
                onClick={
                  (e) =>
                    e.stopPropagation() /* already handled in components click listener */
                }
                onInput={(e) => e.stopPropagation()}
              />

              <label htmlFor="input" class="has-text">
                <slot />
              </label>
            </div>
          </div>
        </div>
      </Host>
    )
  }
}
